import React, { useState } from "react";
import { useSelector } from "react-redux";

import {
  Grid,
  Box,
  Typography,
  makeStyles,
  TableContainer,
  Table,
  TableBody,
  TableHead,
} from "@material-ui/core";

import { SAHeaderBox } from "./SAHeaderBox";
import { headerRows, bodyRows } from "./sample";
import { COLORS } from "../../../../theme/colors";
import { TextInput } from "../../../../components/base";
import { StyledTableRow, StyledTableCell } from "../../../../components/base/Table";
import { ActionButtonsGroup } from "../../../../components/buttonsGroup/ActionButtonsGroup";
import { capitalize } from "../../../../services";

const useStyles = makeStyles((theme) => ({
  title: {
    margin: `${theme.spacing(3.5)}px 0 ${theme.spacing(2.5)}px`,
  },
  titleUppercase: {
    textTransform: "uppercase",
    fontWeight: 700,
  },
  titleSpan: {
    textTransform: "initial",
    color: COLORS.darks[0][50],
    display: "flex",
    alignItems: "center",
    paddingLeft: 2,
  },
  tableContainer: {
    borderRadius: 9,
    border: `1px solid ${COLORS.darks[0][10]}`,
    boxShadow: "none",
  },
  caption: {
    fontWeight: 700,
  },
  cell: {
    "&:not(:last-child)": {
      borderRight: `1px solid ${COLORS.darks[0][10]}`,
    },
  },
  row: {
    backgroundColor: COLORS.white,
    "&:last-child": {
      backgroundColor: "#A2CA8F",
    },
    "&:nth-last-child(3)": {
      backgroundColor: COLORS.darks[0][10],
    },
  },
  lastChildRow: {
    "& p": {
      fontWeight: 700,
    },
  },
  assumptionsHead: {
    padding: 0,
    textAlign: "center",
    backgroundColor: "rgba(233, 151, 45, .06)",
  },
  assumptionsHeadTypography: {
    marginTop: -10,
  },
  assumptions: {
    width: 90,
    textAlign: "center",
    padding: 0,
    borderLeft: `1px solid ${COLORS.darks[0][50]}`,
    backgroundColor: "rgba(233, 151, 45, .06)",
  },
  assumptionsSuffix: {
    padding: 12,
    borderLeft: `1px solid ${COLORS.darks[0][10]}`,
    borderRight: `1px solid ${COLORS.darks[0][50]}`,
  },
  footer: {
    marginTop: theme.spacing(5.75),
    marginBottom: theme.spacing(1.75),
  },
  groupTitle: {
    "&.child-rows": {
      fontWeight: 300,
      textTransform: "uppercase",
    },
    "&:not(.child-rows)": {
      fontWeight: 700,
    },
    "&.transparent": {
      color: "transparent",
    },
  },
  purpleBackground: {
    backgroundColor: "#FCF2FA",
  },
  star: {
    height: 25,
    fontSize: 24,
    color: COLORS.purple,
  },
  inputRootStyle: {
    color: COLORS.purple,
    textAlign: "center",
  },
  rootStyle: {
    "& .MuiInput-underline::after": {
      borderBottom: 0,
    },
  },
}));

const initialFormData = {
  sold: {
    value: "5",
  },
  purchased: {
    value: "2.5",
  },
  praas: {
    value: "10",
  },
  gas: {
    value: "XXX",
  },
};

export const DetailedSensitivityAnalysisTab = () => {
  const classes = useStyles();
  const { user } = useSelector((state) => state.auth);
  const currencySymbol = user.currency.symbol;
  const [formData, setFormData] = useState({ ...initialFormData });
  const [tableData, setTableData] = useState([...bodyRows]);

  const handleChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      [key]: {
        value: value,
      },
    }));
  };

  const handleChangeTableData = (rowI, childRowI, key, value) => {
    const tempBodyRows = [...bodyRows];
    tempBodyRows[rowI]["childRows"][childRowI][key] = value;
    setTableData(tempBodyRows);
  };

  return (
    <Box>
      <Grid container alignItems="center" justifyContent="space-between" className={classes.title}>
        <Box style={{ display: "flex", alignItems: "center" }}>
          <Typography className={classes.titleUppercase}>
            Detailed Sensitivity Analysis -
          </Typography>
          <Typography component="span" className={classes.titleSpan}>
            Fields marked with
            <Typography component="span" className={classes.star} style={{ padding: 2 }}>
              *
            </Typography>{" "}
            are editable
          </Typography>
        </Box>
        <ActionButtonsGroup hideIntegrateBtn />
      </Grid>
      <SAHeaderBox title="Power parameters:" formData={formData} handleChange={handleChange} />
      <TableContainer className={classes.tableContainer}>
        <Table>
          <TableHead>
            <StyledTableRow>
              <StyledTableCell></StyledTableCell>
              {headerRows.map((row, i) => (
                <React.Fragment key={i}>
                  {row === "Assumptions" ? (
                    <StyledTableCell className={classes.assumptionsHead}>
                      <Typography
                        variant="subtitle2"
                        className={`${classes.caption} ${classes.assumptionsHeadTypography}`}
                      >
                        {row}
                        <span className={classes.star}>*</span>
                      </Typography>
                    </StyledTableCell>
                  ) : (
                    <StyledTableCell>
                      <Typography variant="subtitle2" className={classes.caption}>
                        {row}
                      </Typography>
                    </StyledTableCell>
                  )}
                </React.Fragment>
              ))}
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {tableData.map((row, i) => (
              <React.Fragment key={i}>
                <StyledTableRow className={classes.row}>
                  <StyledTableCell colSpan={!row.assumptions ? headerRows.length + 1 : 0}>
                    <Typography
                      className={`
                      ${classes.groupTitle}
                      ${row.childRows ? "child-rows" : ""}
                      ${row.title === "empty" ? "transparent" : ""}
                      `}
                    >
                      {row.title}
                    </Typography>
                  </StyledTableCell>
                  {row.assumptions ? (
                    <React.Fragment>
                      <StyledTableCell className={classes.assumptions}>
                        <Typography>{row.assumptions}</Typography>
                      </StyledTableCell>
                      <StyledTableCell className={classes.assumptionsSuffix}>
                        <Typography>{row.assumptionsSuffix}</Typography>
                      </StyledTableCell>
                      <StyledTableCell className={classes.cell}>
                        <Typography></Typography>
                      </StyledTableCell>
                      <StyledTableCell className={classes.cell}>
                        <Typography></Typography>
                      </StyledTableCell>
                      <StyledTableCell className={classes.cell}>
                        <Typography></Typography>
                      </StyledTableCell>
                      <StyledTableCell className={classes.cell}>
                        <Typography></Typography>
                      </StyledTableCell>
                      <StyledTableCell className={classes.cell}>
                        <Typography></Typography>
                      </StyledTableCell>
                      <StyledTableCell className={classes.cell}>
                        <Typography></Typography>
                      </StyledTableCell>
                      <StyledTableCell className={classes.cell}>
                        <Typography></Typography>
                      </StyledTableCell>
                      <StyledTableCell className={classes.cell}>
                        <Typography></Typography>
                      </StyledTableCell>
                      <StyledTableCell className={classes.cell}>
                        <Typography></Typography>
                      </StyledTableCell>
                    </React.Fragment>
                  ) : null}
                </StyledTableRow>
                {row.childRows
                  ? row.childRows.map((childRow, j) => (
                      <StyledTableRow
                        key={j}
                        className={`
                        ${classes.row}
                        ${row.childRows.length - 1 === j ? classes.lastChildRow : ""}
                        ${
                          [tableData.length - 4, tableData.length - 5].includes(i)
                            ? classes.purpleBackground
                            : ""
                        }
                      `}
                      >
                        <StyledTableCell>
                          <Typography>{childRow.childTitle}</Typography>
                        </StyledTableCell>
                        <StyledTableCell className={classes.assumptions}>
                          <TextInput
                            type="number"
                            variant="standard"
                            fullWidth={true}
                            inputRootStyle={classes.inputRootStyle}
                            rootStyle={classes.rootStyle}
                            value={childRow.assumptions}
                            onChange={(value) => handleChangeTableData(i, j, "assumptions", value)}
                          />
                        </StyledTableCell>
                        <StyledTableCell className={classes.assumptionsSuffix}>
                          <Typography>
                            {childRow.assumptionsSuffix === `${currencySymbol}/Sqft`
                              ? `${currencySymbol}/${capitalize(user?.unit_system)}`
                              : childRow.assumptionsSuffix}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableCell className={classes.cell}>
                          <Typography></Typography>
                        </StyledTableCell>
                        <StyledTableCell className={classes.cell}>
                          <Typography></Typography>
                        </StyledTableCell>
                        <StyledTableCell className={classes.cell}>
                          <Typography></Typography>
                        </StyledTableCell>
                        <StyledTableCell className={classes.cell}>
                          <Typography></Typography>
                        </StyledTableCell>
                        <StyledTableCell className={classes.cell}>
                          <Typography></Typography>
                        </StyledTableCell>
                        <StyledTableCell className={classes.cell}>
                          <Typography></Typography>
                        </StyledTableCell>
                        <StyledTableCell className={classes.cell}>
                          <Typography></Typography>
                        </StyledTableCell>
                        <StyledTableCell className={classes.cell}>
                          <Typography></Typography>
                        </StyledTableCell>
                        <StyledTableCell className={classes.cell}>
                          <Typography></Typography>
                        </StyledTableCell>
                      </StyledTableRow>
                    ))
                  : null}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Grid container alignItems="flex-end" className={classes.footer}>
        <ActionButtonsGroup hideIntegrateBtn />
      </Grid>
    </Box>
  );
};
